import React, { FC, useContext, useEffect, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";
import { useWalkthrough } from "stores/WalkthroughComplete";
import { useTokenManager } from "../contexts/TokenManagerContext";
import { observer } from "mobx-react-lite";
// import CURRENT_ARTIST, {
//   CURRENT_ARTIST_RESPONSE,
// } from "core/graphql/queries/currentArtist";
// import CURRENT_USER from "core/graphql/queries/currentUser";
import ArtistContext from "../stores/legacyArtistContext";
import envs from "../config/envs";
import { isArtistSurfacePath } from "../core/auth/artistPortalPaths";

interface IArtistFlowHandler {
  children?: React.ReactNode;
}

const ArtistFlowHandler: FC<IArtistFlowHandler> = ({ children }) => {
  // const { data, called, loading } =
  //   useQuery<CURRENT_ARTIST_RESPONSE>(CURRENT_ARTIST);
  // const { data: userData } = useQuery<CurrentUserReturn>(CURRENT_USER);
  const data: any = null;
  const called = false;
  const loading = false;
  const userData: any = null;

  const {
    setWalkthroughComplete,
    setConnectStripeComplete,
  } = useWalkthrough();

  const artistManager = useContext(ArtistContext);

  const tokenManager = useTokenManager();

  const [redirectUri, setRedirectUri] = useState("");

  const location = useLocation();

  useEffect(() => {
    if (data?.artist && data.artist.proAccount === true) {
      artistManager.setProAccount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- legacy store instance
  }, [data?.artist]);

  useEffect(() => {
    setConnectStripeComplete(!!userData?.user?.stripeAccountId);
  }, [userData?.user?.stripeAccountId, setConnectStripeComplete]);

  useEffect(() => {
    const wt = data?.artist?.walkthroughComplete;
    if (tokenManager.isArtist && typeof wt === "boolean") {
      setWalkthroughComplete(wt);
    }
  }, [tokenManager.isArtist, data?.artist?.walkthroughComplete, setWalkthroughComplete]);

  useEffect(() => {
    // Hard redirect across subdomains when the user is already signed in.
    // This prevents artists from staying on `app.starfarm.com` and landing on
    // the wrong dashboard. Include path heuristic so legacy "loop breaker"
    // scripts that clear `isArtist` on the main domain still bounce
    // `/artist/*` and `/upload*` to the artist host.
    const hasToken = !!tokenManager.getToken();
    const needsArtistHost =
      tokenManager.isArtist ||
      (hasToken && isArtistSurfacePath(location.pathname));

    if (needsArtistHost) {
      try {
        const targetOrigin = new URL(envs.artistPortalUri).origin;
        if (window.location.origin !== targetOrigin) {
          window.location.replace(
            `${envs.artistPortalUri}${location.pathname}${location.search}`
          );
          return;
        }
      } catch {
        // If URL parsing fails, fall back to route-level redirect below.
      }
    }

    if (tokenManager.isArtist) {
      // Only gate onboarding when we have an artist record; with the artist
      // query disabled, `data` is null — do not treat default walkthrough state as incomplete.
      const wt = data?.artist?.walkthroughComplete;
      if (wt === true) {
        if (location.pathname.includes("/artist/onboarding")) {
          setRedirectUri("/artist/dashboard");
          return;
        }
        return;
      }
      if (wt === false) {
        setRedirectUri("/artist/onboarding");
        return;
      }
      return;
    }

    if (tokenManager.isBeneficiary && !tokenManager.isArtist) {
      if (isArtistSurfacePath(location.pathname)) {
        return;
      }
      if (!location.pathname.includes("/dashboard")) {
        setRedirectUri("/dashboard");
      }

      return;
    }

    return;
  }, [
    location.pathname,
    tokenManager.isArtist,
    tokenManager.isBeneficiary,
    data,
    called,
    loading,
  ]);

  if (!!redirectUri) {
    return <Redirect to={redirectUri} />;
  }

  return null;
};

export default observer(ArtistFlowHandler);
